<script setup lang="ts">
/**
 * Money: expenses and income — the team's when a team is picked, the reader's
 * own on Personal.
 *
 * A team's ledger is shared by everyone in the team, and each line says who
 * wrote it. The personal one is nobody else's business — the server scopes it
 * to the caller and no admin can open it (server/finance.ts). The head of the
 * page says which it is, because the two look otherwise identical.
 *
 * One year at a time. Clicking a month or a category narrows the list and the
 * totals to it; clicking it again widens back out.
 *
 * Rent, a car loan, insurance: the form's "Repeats" turns a line into a regular
 * payment, which the server posts into the ledger as an ordinary line each
 * time it falls due. Past ones are filled in when it is set up.
 */
import { computed, ref, watch } from "vue";
import { useBoard } from "../stores/board.js";
import { useFinance } from "../stores/finance.js";
import { toast } from "../lib/toast.js";
import {
  COMMON_CURRENCIES,
  DEFAULT_CATEGORIES,
  amountText,
  dueDays,
  everyLabel,
  formatAmount,
  ledgerProblem,
  parseAmount,
  perMonth,
  recurringProblem,
  summarise,
  todayIso,
} from "@shared/finance.js";
import {
  LEDGER_CATEGORY_MAX,
  LEDGER_NOTE_MAX,
  RECURRING_EVERY,
  type LedgerEntry,
  type LedgerKind,
  type Recurring,
  type RecurringEvery,
} from "@shared/types.js";

const board = useBoard();
const finance = useFinance();
/** A team's shared ledger, rather than the reader's own. */
const shared = computed(() => finance.teamId !== null);
const whose = computed(() => (shared.value ? "the team's" : "your"));

const money = (minor: number): string => formatAmount(minor, finance.settings.currency);
const signed = (e: LedgerEntry): string => (e.kind === "income" ? "+" : "−") + money(e.amountMinor);

// ── narrowing ──────────────────────────────────────────────────────────────
const month = ref<string | null>(null);
const category = ref<string | null>(null);

const inMonth = computed(() =>
  month.value ? finance.sorted.filter((e) => e.date.startsWith(month.value!)) : finance.sorted,
);
const shown = computed(() =>
  category.value === null ? inMonth.value : inMonth.value.filter((e) => e.category === category.value),
);
/** Totals for whatever the list is narrowed to, so the tiles always describe the rows beneath them. */
const periodSummary = computed(() => summarise(shown.value));
/** Categories are ranked within the month but not within themselves, or clicking one would hide the rest. */
const categorySummary = computed(() => summarise(inMonth.value));
const topCategory = computed(() => categorySummary.value.expenseByCategory[0]?.amount ?? 0);

const monthLabel = (ym: string): string =>
  new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(Number(ym.slice(0, 4)), Number(ym.slice(5)) - 1, 1));
const dayDate = (iso: string): Date => new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8)));
const dayLabel = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(dayDate(iso));
/** A day that may be in another year, which then has to say so. */
const fullDayLabel = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    ...(iso.startsWith(`${new Date().getFullYear()}-`) ? {} : { year: "numeric" }),
  }).format(dayDate(iso));

const periodLabel = computed(() => {
  const parts = [month.value ? `${monthLabel(month.value)} ${finance.year}` : String(finance.year)];
  if (category.value !== null) parts.push(category.value || "Uncategorised");
  return parts.join(" · ");
});

function pickMonth(ym: string): void {
  month.value = month.value === ym ? null : ym;
}
function pickCategory(name: string): void {
  category.value = category.value === name ? null : name;
}

async function changeYear(next: number): Promise<void> {
  month.value = null;
  category.value = null;
  cancelEdit();
  try {
    await finance.showYear(next);
  } catch (err) {
    toast(`Could not load ${next}: ${(err as Error).message}`);
  }
}

// ── currency ───────────────────────────────────────────────────────────────
const currencyChoice = ref("GBP");

async function saveCurrency(code: string): Promise<void> {
  try {
    await finance.setCurrency(code);
  } catch (err) {
    toast((err as Error).message, 6000);
  }
}

const currencyOptions = computed(() => {
  const current = finance.settings.currency;
  return current && !COMMON_CURRENCIES.includes(current) ? [current, ...COMMON_CURRENCIES] : COMMON_CURRENCIES;
});

// ── regular payments ───────────────────────────────────────────────────────
const regularName = (r: Pick<Recurring, "category" | "note">): string => r.category || r.note || "Uncategorised";

/** What the running ones come to a month — a yearly bill counted as a twelfth. */
const monthly = computed(() => {
  const t = { expense: 0, income: 0 };
  for (const r of finance.regulars) if (r.next) t[r.kind] += perMonth(r);
  return t;
});

// ── the form ───────────────────────────────────────────────────────────────
const editing = ref<LedgerEntry | null>(null);
const editingRegular = ref<Recurring | null>(null);
/** 0 is a single line; anything else makes the form set up a regular payment. */
const repeat = ref<0 | RecurringEvery>(0);
const until = ref("");
const kind = ref<LedgerKind>("expense");
const amount = ref("");
const date = ref(todayIso());
const cat = ref("");
const note = ref("");
const saving = ref(false);
const amountInput = ref<HTMLInputElement | null>(null);

const suggestions = computed(() => [...new Set([...finance.usedCategories, ...DEFAULT_CATEGORIES])]);

const parsedAmount = computed(() => parseAmount(amount.value, finance.digits));
const formProblem = computed(() => {
  if (!amount.value.trim()) return "";
  if (parsedAmount.value === null) {
    return finance.digits ? `Not an amount — up to ${finance.digits} decimal places` : "Not an amount — whole units only";
  }
  return ledgerProblem({ kind: kind.value, amountMinor: parsedAmount.value, date: date.value, category: cat.value, note: note.value }) ?? "";
});
const regularInput = computed(() => ({
  kind: kind.value,
  amountMinor: parsedAmount.value ?? 0,
  start: date.value,
  everyMonths: repeat.value as RecurringEvery,
  until: until.value || null,
  category: cat.value,
  note: note.value,
}));
const regularProblem = computed(() => (repeat.value && parsedAmount.value !== null ? recurringProblem(regularInput.value) : null));

/**
 * How many payments, up to and including today's, saving this will post at
 * once. Worth saying before the click: someone who already typed March's rent
 * in by hand would get it twice.
 */
const backfill = computed(() => {
  if (!repeat.value || regularProblem.value) return 0;
  const r = { ...regularInput.value, postedThrough: editingRegular.value?.postedThrough ?? null };
  return dueDays(r, todayIso()).length;
});

const canSave = computed(
  () => parsedAmount.value !== null && !formProblem.value && !regularProblem.value && !saving.value,
);

function editRegular(r: Recurring): void {
  editing.value = null;
  editingRegular.value = r;
  kind.value = r.kind;
  amount.value = amountText(r.amountMinor, finance.digits);
  date.value = r.start;
  repeat.value = r.everyMonths;
  until.value = r.until ?? "";
  cat.value = r.category;
  note.value = r.note;
  amountInput.value?.focus();
  amountInput.value?.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

function edit(e: LedgerEntry): void {
  editingRegular.value = null;
  repeat.value = 0;
  until.value = "";
  editing.value = e;
  kind.value = e.kind;
  amount.value = amountText(e.amountMinor, finance.digits);
  date.value = e.date;
  cat.value = e.category;
  note.value = e.note;
  amountInput.value?.focus();
  amountInput.value?.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

/** Back to a blank line. Kind and date are kept: several receipts in a row are usually from the same day. */
function cancelEdit(): void {
  if (editingRegular.value) {
    // Leaving a regular payment: its dates and frequency are not the next line's.
    date.value = todayIso();
    repeat.value = 0;
    until.value = "";
  }
  editing.value = null;
  editingRegular.value = null;
  amount.value = "";
  cat.value = "";
  note.value = "";
}

async function save(): Promise<void> {
  if (!canSave.value) return;
  saving.value = true;
  const input = { kind: kind.value, amountMinor: parsedAmount.value!, date: date.value, category: cat.value, note: note.value };
  try {
    if (editingRegular.value) {
      await finance.patchRecurring(editingRegular.value.id, regularInput.value);
    } else if (repeat.value) {
      const added = backfill.value;
      await finance.addRecurring(regularInput.value);
      if (added) toast(`Added ${added} payment${added === 1 ? "" : "s"} to ${whose.value} ledger`);
      date.value = todayIso();
      repeat.value = 0;
      until.value = "";
    } else {
      if (editing.value) await finance.patch(editing.value.id, input);
      else await finance.add(input);
      if (!input.date.startsWith(`${finance.year}-`)) toast(`Saved under ${input.date.slice(0, 4)}`);
    }
    cancelEdit();
    amountInput.value?.focus();
  } catch (err) {
    // Keep what was typed: a failed save must not cost someone the line.
    toast(`Not saved: ${(err as Error).message}`);
  } finally {
    saving.value = false;
  }
}

async function stopRegular(): Promise<void> {
  const target = editingRegular.value;
  if (!target) return;
  if (!confirm(`Stop “${regularName(target)}”? Payments already in ${whose.value} ledger stay there.`)) return;
  try {
    await finance.removeRecurring(target.id);
    cancelEdit();
  } catch (err) {
    toast(`Not stopped: ${(err as Error).message}`);
  }
}

async function removeEditing(): Promise<void> {
  const target = editing.value;
  if (!target || !confirm(`Delete this ${target.kind} of ${money(target.amountMinor)}?`)) return;
  try {
    await finance.remove(target.id);
    cancelEdit();
  } catch (err) {
    toast(`Not deleted: ${(err as Error).message}`);
  }
}

watch(
  () => board.teamId,
  async (team) => {
    // What was being edited belongs to the ledger just left.
    cancelEdit();
    try {
      await finance.open(team);
    } catch (err) {
      toast(`Could not load ${whose.value} ledger: ${(err as Error).message}`);
    }
  },
  { immediate: true },
);
</script>

<template>
  <section class="money" :aria-label="shared ? `${board.team?.name ?? 'Team'} money` : 'Your money'">
    <div class="money-inner">
      <header class="money-head">
        <div class="money-years">
          <button
            class="btn ghost icon-btn"
            type="button"
            aria-label="Previous year"
            @click="changeYear(finance.year - 1)"
          >‹</button>
          <select
            class="head-select money-year"
            :value="finance.year"
            aria-label="Year"
            @change="changeYear(Number(($event.target as HTMLSelectElement).value))"
          >
            <option v-for="y in finance.yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
          <button
            class="btn ghost icon-btn"
            type="button"
            aria-label="Next year"
            @click="changeYear(finance.year + 1)"
          >›</button>
        </div>
        <span v-if="shared" class="money-private" title="Shared by everyone in this team, and nobody outside it.">
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <circle cx="5.5" cy="5.5" r="2.2" fill="none" stroke="currentColor" stroke-width="1.3" />
            <circle cx="11" cy="6.5" r="1.8" fill="none" stroke="currentColor" stroke-width="1.3" />
            <path d="M1.5 14c0-2.5 1.8-4 4-4s4 1.5 4 4M9.5 10.3c.5-.2 1-.3 1.5-.3 1.9 0 3.5 1.3 3.5 3.6" fill="none" stroke="currentColor" stroke-width="1.3" />
          </svg>
          Everyone in {{ board.team?.name ?? "the team" }} can see this
        </span>
        <span v-else class="money-private" title="Stored against your account only. Admins cannot see it either.">
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <rect x="3" y="7" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.3" />
            <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" stroke-width="1.3" />
          </svg>
          Only you can see this
        </span>
        <span style="flex: 1"></span>
        <select
          v-if="finance.settings.currency"
          class="head-select"
          :value="finance.settings.currency"
          :title="`The currency ${whose} amounts are in`"
          aria-label="Currency"
          @change="saveCurrency(($event.target as HTMLSelectElement).value)"
        >
          <option v-for="c in currencyOptions" :key="c" :value="c">{{ c }}</option>
        </select>
        <a
          class="btn ghost"
          :href="finance.exportUrl"
          :download="`ledger-${finance.year}.csv`"
          title="Download this year as a spreadsheet"
        >Export CSV</a>
      </header>

      <div v-if="!finance.loaded" class="empty">…</div>

      <!-- A bare "12.50" means nothing, so the first thing asked is what it is in. -->
      <div v-else-if="!finance.settings.currency" class="money-card money-setup">
        <template v-if="shared">
          <h2>Keep track of {{ board.team?.name ?? "the team" }}'s money</h2>
          <p>Record what the team spends and what comes in. Everyone in the team sees the same ledger and can add to it.</p>
        </template>
        <template v-else>
          <h2>Keep track of your own money</h2>
          <p>Record what you spend and what comes in. It is kept against your account and nobody else on the board can see it.</p>
        </template>
        <label class="money-field">
          <span>Which currency {{ shared ? "does the team keep its" : "do you keep your" }} books in?</span>
          <select v-model="currencyChoice">
            <option v-for="c in COMMON_CURRENCIES" :key="c" :value="c">{{ c }}</option>
          </select>
        </label>
        <button class="btn primary" type="button" @click="saveCurrency(currencyChoice)">Start</button>
      </div>

      <template v-else>
        <form class="money-card money-form" :class="{ editing: editing || editingRegular }" @submit.prevent="save">
          <div class="money-kind" role="radiogroup" aria-label="Kind">
            <button
              v-for="k in (['expense', 'income'] as const)"
              :key="k"
              type="button"
              role="radio"
              :aria-checked="kind === k"
              :class="{ on: kind === k }"
              @click="kind = k"
            >{{ k === "expense" ? "Spent" : "Received" }}</button>
          </div>
          <label class="money-field money-amount">
            <span>Amount ({{ finance.settings.currency }})</span>
            <input
              ref="amountInput"
              v-model="amount"
              inputmode="decimal"
              autocomplete="off"
              :placeholder="finance.digits ? '0.' + '0'.repeat(finance.digits) : '0'"
              :aria-invalid="!!formProblem"
            />
          </label>
          <label class="money-field">
            <span>{{ repeat ? "First payment" : "Date" }}</span>
            <input v-model="date" type="date" required />
          </label>
          <label class="money-field">
            <span>Category</span>
            <input v-model="cat" list="money-categories" :maxlength="LEDGER_CATEGORY_MAX" placeholder="e.g. Food" autocomplete="off" />
            <datalist id="money-categories">
              <option v-for="c in suggestions" :key="c" :value="c" />
            </datalist>
          </label>
          <label v-if="!editing" class="money-field money-schedule">
            <span>Repeats</span>
            <select v-model.number="repeat">
              <option v-if="!editingRegular" :value="0">Never</option>
              <option v-for="n in RECURRING_EVERY" :key="n" :value="n">{{ everyLabel(n) }}</option>
            </select>
          </label>
          <label v-if="repeat" class="money-field money-schedule">
            <span>Until (optional)</span>
            <input v-model="until" type="date" :min="date" />
          </label>
          <label class="money-field money-note">
            <span>Note</span>
            <input v-model="note" :maxlength="LEDGER_NOTE_MAX" placeholder="What was it?" autocomplete="off" />
          </label>
          <div class="money-actions">
            <p v-if="formProblem || regularProblem" class="money-problem" role="alert">{{ formProblem || regularProblem }}</p>
            <p v-else-if="editing?.recurringId" class="money-hint">
              One payment of a regular one — this changes only this line.
            </p>
            <p v-else-if="editingRegular" class="money-hint">
              Changes apply to payments from now on; ones already in {{ whose }} ledger stay as they were.
              <template v-if="backfill"> Adds {{ backfill }} payment{{ backfill === 1 ? "" : "s" }} now.</template>
            </p>
            <p v-else-if="backfill" class="money-hint">
              Adds {{ backfill }} payment{{ backfill === 1 ? "" : "s" }} to {{ whose }} ledger now, from {{ fullDayLabel(date) }}.
            </p>
            <span style="flex: 1"></span>
            <button v-if="editing" class="btn ghost danger" type="button" @click="removeEditing">Delete</button>
            <button v-if="editingRegular" class="btn ghost danger" type="button" @click="stopRegular">Stop</button>
            <button v-if="editing || editingRegular" class="btn" type="button" @click="cancelEdit">Cancel</button>
            <button class="btn primary" type="submit" :disabled="!canSave">{{ editing || editingRegular ? "Save" : "Add" }}</button>
          </div>
        </form>

        <div class="money-period">
          <span>{{ periodLabel }}</span>
          <button
            v-if="month || category !== null"
            class="btn ghost"
            type="button"
            @click="month = null; category = null"
          >Show all of {{ finance.year }}</button>
        </div>

        <div class="money-tiles">
          <div class="money-tile">
            <span class="money-tile-label">In</span>
            <span class="money-tile-value">{{ money(periodSummary.total.income) }}</span>
          </div>
          <div class="money-tile">
            <span class="money-tile-label">Out</span>
            <span class="money-tile-value">{{ money(periodSummary.total.expense) }}</span>
          </div>
          <div class="money-tile">
            <span class="money-tile-label">Net</span>
            <span class="money-tile-value" :class="periodSummary.total.net < 0 ? 'neg' : 'pos'">
              {{ periodSummary.total.net < 0 ? "−" : "" }}{{ money(Math.abs(periodSummary.total.net)) }}
            </span>
          </div>
        </div>

        <div v-if="finance.summary.total.count" class="money-breakdowns">
          <div class="money-card">
            <h3>By month</h3>
            <table class="money-table">
              <thead>
                <tr><th>Month</th><th>In</th><th>Out</th><th>Net</th></tr>
              </thead>
              <tbody>
                <tr
                  v-for="m in finance.summary.byMonth"
                  :key="m.month"
                  :class="{ current: month === m.month }"
                  tabindex="0"
                  @click="pickMonth(m.month)"
                  @keydown.enter="pickMonth(m.month)"
                >
                  <td>{{ monthLabel(m.month) }}</td>
                  <td>{{ money(m.totals.income) }}</td>
                  <td>{{ money(m.totals.expense) }}</td>
                  <td :class="m.totals.net < 0 ? 'neg' : 'pos'">
                    {{ m.totals.net < 0 ? "−" : "" }}{{ money(Math.abs(m.totals.net)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="categorySummary.expenseByCategory.length" class="money-card">
            <h3>Spending by category<template v-if="month"> · {{ monthLabel(month) }}</template></h3>
            <ul class="money-cats">
              <li
                v-for="c in categorySummary.expenseByCategory"
                :key="c.category"
                :class="{ current: category === c.category }"
                tabindex="0"
                @click="pickCategory(c.category)"
                @keydown.enter="pickCategory(c.category)"
              >
                <span class="money-cat-name">{{ c.category || "Uncategorised" }}</span>
                <span class="money-cat-amount">{{ money(c.amount) }}</span>
                <span class="money-cat-bar" aria-hidden="true">
                  <span :style="{ width: (c.amount / topCategory) * 100 + '%' }"></span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div v-if="finance.regulars.length" class="money-card">
          <h3>Regular payments</h3>
          <ul class="money-regulars">
            <li
              v-for="r in finance.regulars"
              :key="r.id"
              :class="{ current: editingRegular?.id === r.id, ended: !r.next }"
              tabindex="0"
              title="Edit this regular payment"
              @click="editRegular(r)"
              @keydown.enter="editRegular(r)"
            >
              <span class="money-desc">
                <span class="money-cat">{{ regularName(r) }}</span>
                <span class="money-note-text">
                  {{ everyLabel(r.everyMonths) }} ·
                  {{ r.next ? "next " + fullDayLabel(r.next) : "ended" }}<template v-if="r.category && r.note"> · {{ r.note }}</template>
                </span>
              </span>
              <span class="money-value" :class="r.kind === 'income' ? 'pos' : ''">
                {{ r.kind === "income" ? "+" : "−" }}{{ money(r.amountMinor) }}
              </span>
            </li>
          </ul>
          <p class="money-regular-total">
            <span>Out ≈ <b>{{ money(monthly.expense) }}</b> a month</span>
            <span v-if="monthly.income">In ≈ <b>{{ money(monthly.income) }}</b> a month</span>
          </p>
        </div>

        <ul v-if="shown.length" class="money-list">
          <li
            v-for="e in shown"
            :key="e.id"
            :class="{ current: editing?.id === e.id }"
            tabindex="0"
            :title="'Edit this ' + e.kind"
            @click="edit(e)"
            @keydown.enter="edit(e)"
          >
            <span class="money-date">{{ dayLabel(e.date) }}</span>
            <span class="money-desc">
              <span class="money-cat">
                {{ e.category || "Uncategorised" }}
                <span v-if="e.recurringId" class="money-repeat" title="Posted by a regular payment" aria-label="regular payment">↻</span>
              </span>
              <span v-if="e.note || shared" class="money-note-text">
                {{ e.note }}<template v-if="shared">{{ e.note ? " · " : "" }}{{ board.userName(e.createdBy) }}</template>
              </span>
            </span>
            <span class="money-value" :class="e.kind === 'income' ? 'pos' : ''">{{ signed(e) }}</span>
          </li>
        </ul>
        <p v-else class="money-empty">
          {{ finance.summary.total.count ? "Nothing here for " + periodLabel + "." : "Nothing recorded in " + finance.year + " yet. Add your first line above." }}
        </p>
      </template>
    </div>
  </section>
</template>
